<template>
  <div>
    <h1>Manual Roster Assignment</h1>
    <p>Assign employees to rosters manually with specific effective dates and roles.<br />Select employees (checkboxes or drag & drop), choose a roster, and define the date range.</p>

    <b-overlay :show="loading">
      <div class="row">
        <div class="col-8">
          <!-- Drag & drop palette below the grid -->
          <b-card class="mt-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="mb-0">Employees (Drag from here)</h6>
              <small class="text-muted">{{ paletteEmployees.length }}</small>
            </div>

            <b-input v-model.trim="search" placeholder="Search employees..." size="sm" class="mb-2"/>

            <draggable v-model="paletteEmployees" :group="{ name: 'assign', pull: 'clone', put: false }" :sort="false" item-key="employee_id" :clone="cloneEmployee" class="palette">
              <div v-for="e in paletteEmployees" :key="e.employee_id" class="pill">
                <img :src="avatar(e)" width="22" height="22" class="rounded-circle mr-2" />
                <div class="text-truncate">
                  <strong>{{ e.name }}</strong>
                  <small class="text-muted ml-1">#{{ e.employee_id }}</small>
                  <div class="small text-muted">{{ e.department }} · {{ e.jobposition }}</div>
                </div>
              </div>
            </draggable>
            <small class="text-muted d-block mt-2">Drag to “Selected” ➜</small>
          </b-card>
        </div>

        <div class="col-4">
          <b-form @submit.stop.prevent="onSubmit" novalidate>
            <b-form-group label="Select Roster" label-for="roster_id" label-size="sm" class="required-field">
              <b-input-group>
                <b-form-select id="roster_id" name="roster_id" v-model="form.roster_id" :options="rosterOptions" v-validate="'required'" data-vv-as="Roster"
                               :state="veeErrors.has('roster_id') ? false : (form.roster_id !== '' ? true : null)" aria-describedby="roster_id-feedback" @change="selectRoster(form.roster_id)">
                  <template #first>
                    <b-form-select-option :value="null" disabled>Select Roster…</b-form-select-option>
                  </template>
                </b-form-select>
              </b-input-group>
              <b-form-invalid-feedback id="roster_id-feedback" v-if="veeErrors.has('roster_id')">
                {{ veeErrors.first('roster_id') }}
              </b-form-invalid-feedback>
            </b-form-group>

            <!-- Period Start -->
            <b-form-group label="Period Start" label-for="period_start" label-size="sm" class="required-field">
              <b-form-datepicker id="period_start" name="period_start" v-model="form.period_start" value-as-date :min="startMin" :max="startMax" v-validate="'required'"
                                 data-vv-as="Period Start" :state="veeErrors.has('period_start') ? false : (form.period_start ? true : null)" aria-describedby="period_start-feedback"/>
              <b-form-invalid-feedback id="period_start-feedback" v-if="veeErrors.has('period_start')">
                {{ veeErrors.first('period_start') }}
              </b-form-invalid-feedback>
            </b-form-group>

            <!-- Period End -->
            <b-form-group label="Period End" label-for="period_end" label-size="sm" class="required-field">
              <b-form-datepicker id="period_end" name="period_end" v-model="form.period_end" value-as-date :min="endMin" :max="endMax" v-validate="'required'"
                                 data-vv-as="Period End" :state="veeErrors.has('period_end') ? false : (form.period_end ? true : null)" aria-describedby="period_end-feedback"/>
              <b-form-invalid-feedback id="period_end-feedback" v-if="veeErrors.has('period_end')">
                {{ veeErrors.first('period_end') }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-card class="mb-3">
              <div class="d-flex align-items-center justify-content-between">
                <h6 class="mb-0">Selected (Drop here)</h6>
                <b-badge variant="light">{{ selectedEmployees.length }}</b-badge>
              </div>
              <draggable v-model="selectedEmployees" :group="{ name: 'assign', pull: true, put: true }" item-key="employee_id" :animation="150" :move="preventDuplicateInSelected" class="dropzone mt-2">
                <div v-for="e in selectedEmployees" :key="e.employee_id" class="pill">
                  <span class="handle mr-1">⋮⋮</span>
                  <div class="text-truncate">
                    <strong>{{ e.name }}</strong>
                    <small class="text-muted ml-1">#{{ e.employee_id }}</small>
                    <div class="small text-muted">{{ e.department }} · {{ e.jobposition }}</div>
                  </div>
                  <b-button size="sm" variant="link" class="ml-auto p-0 no-drag" @click="removeFromSelected(e.employee_id)">
                    <b-icon icon="x-circle" />
                  </b-button>
                </div>
                <div class="empty" v-if="!selectedEmployees.length">Drop employees here</div>
              </draggable>

            </b-card>

            <b-button type="submit" variant="primary" class="w-100 mt-2" :disabled="finalEmployeeIds.length == 0 || veeErrors.any()">
              Assign to ({{ finalEmployeeIds.length }}) Employees
            </b-button>

          </b-form>
        </div>
      </div>
    </b-overlay>

    <b-modal id="bv-preview-roster-modal" size="lg" ok-variant="primary" hide-footer>
      <iframe v-if="previewUrl != ''" :src="previewUrl" width="100%" height="600" style="border:none"></iframe>
    </b-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  components:{draggable},
  data(){
    return {
      loading:false,
      generating: false,
      primary_key:'employee_id',
      company_options:[],
      y:2025,
      m:8,
      previewUrl: '',
      list_key:0,
      is_action: 0,
      custom_list: [],
      default_column:[ 'name','employee_code', 'department', 'jobposition', 'roster'],

      form:{
        roster_id: null,
        period_start: null,
        period_end: null,
      },

      employee_ids: [],

      // DnD state
      search: '',
      selectedEmployees: [],
      trash: [],

      rosterOptions: [],
      branchOptions: [],
      selectedRoster: {
        period_start: null,
        period_end: null,
        department_id: null,
      }
    }
  },
  computed:{
    // date constraints
    startMin() {
      return this.selectedRoster && this.selectedRoster.period_start ? this.selectedRoster.period_start : null;
    },
    startMax() {
      return this.selectedRoster && this.selectedRoster.period_end ? this.selectedRoster.period_end : null;
    },
    endMin() {
      if (this.form.period_start) return this.form.period_start;
      return this.selectedRoster && this.selectedRoster.period_start ? this.selectedRoster.period_start : null;
    },
    endMax() {
      return this.selectedRoster && this.selectedRoster.period_end ? this.selectedRoster.period_end : null;
    },

    // palette = employees not already selected (with search)
    paletteEmployees() {
      const q = this.search.trim().toLowerCase();
      const picked = new Set(this.selectedEmployees.map(e => e.employee_id));
      let list = this.custom_list
          .map(x => ({
            employee_id: x.employee_id,
            company_id: x.company_id,
            name: x.name,
            employee_code: x.employee_code,
            department: x.department,
            jobposition: x.jobposition,
            pic: x.pic
          }))
          .filter(e => !picked.has(e.employee_id));

      if (q) {
        list = list.filter(e =>
            (e.name).toLowerCase().includes(q) ||
            String(e.employee_id).includes(q) ||
            (e.department).toLowerCase().includes(q) ||
            (e.jobposition).toLowerCase().includes(q)
        );
      }
      return list;
    },

    // union of checkbox-selected and DnD-selected IDs
    finalEmployeeIds() {
      const a = Array.isArray(this.employee_ids) ? this.employee_ids : [];
      const b = this.selectedEmployees.map(e => e.employee_id);
      return Array.from(new Set([...a, ...b]));
    },
  },
  methods:{
    avatar(e){
      return `/avatar/${e.company_id}/${e.pic}`;
    },

    listReturn(data) {
      this.employee_ids = data.selected;
    },

    selectRoster(id){
      const found = this.rosterOptions.find(el => el.id == id);
      this.selectedRoster = found || { period_start: null, period_end: null, department_id: null };
      this.form.period_start = this.selectedRoster.period_start || null;
      this.form.period_end   = this.selectedRoster.period_end || null;
      this.getEmployees(this.selectedRoster.department_id || null);
    },

    getRosters(){
      axios.get(this.$api_url + '/leave-attendance/all').then(response => {
        this.rosterOptions = response.data;
      });
    },

    getEmployees(dept_id = null) {
      let url = this.$api_url + '/leave-attendance/employees/index';
      if (dept_id != null) url += '?department_id=' + dept_id;
      axios.get(url).then(response => {
        this.custom_list = response.data.employee_data;
        this.company_options = response.data.options;
        // optional: prune selected employees no longer in filtered list
        const ids = new Set(this.custom_list.map(x => x.employee_id));
        this.selectedEmployees = this.selectedEmployees.filter(e => ids.has(e.employee_id));
      });
    },

    // --- DnD helpers ---
    cloneEmployee(e) { return { ...e }; },
    preventDuplicateInSelected(evt) {
      const toList = evt.relatedContext.list;
      const dragged = evt.draggedContext.element;
      return !toList.some(x => x.employee_id == dragged.employee_id);
    },
    removeFromSelected(empId) {
      const i = this.selectedEmployees.findIndex(x => x.employee_id == empId);
      if (i !== -1) this.selectedEmployees.splice(i, 1);
    },
    onTrashAdd(evt) {
      const emp = evt.added && evt.added.element;
      if (emp) this.removeFromSelected(emp.employee_id);
      this.trash.splice(0);
    },

    refreshPreview() {
      if (this.form.branch_id == null) {
        this.$bvToast.toast("Please select a work location to generate preview",{
          solid: true, variant: 'danger', title: 'Failed'
        });
        return;
      }
      this.generating = true;
      const payload = {
        branch_id: this.form.branch_id,
        year: this.y,
        month: parseInt(this.m, 10),
      };
      axios.post(this.$api_url + '/leave-attendance/schedule/preview', payload, { responseType: 'blob' })
          .then(resp => {
            const blob = new Blob([resp.data], { type: 'application/pdf' });
            if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = URL.createObjectURL(blob);
            this.$bvModal.show('bv-preview-roster-modal');
          })
          .catch(e => {
            this.$bvToast.toast('Failed to generate preview', { variant: 'danger' });
          })
          .finally(() => { this.generating = false; });
    },

    onSubmit() {
      this.loading = true;

      this.$validator.validateAll().then(valid => {
        if (!valid) {
          this.loading = false;
          return;
        }

        const ids = this.finalEmployeeIds;
        if (!ids.length) {
          this.loading = false;
          return;
        }

        const formData = new FormData();
        formData.append('employee_ids', JSON.stringify(ids));
        Object.entries(this.form).forEach(([key, val]) => {
          formData.append(key, val);
        });

        axios.post(this.$api_url + '/leave-attendance/generate', formData).then(res => {
              this.resetForm();
              this.$validator.reset();
              this.selectedEmployees = [];
              this.$bvToast.toast(res.data.message, { variant: 'success', title: 'Success', solid: true });
              this.getEmployees(this.selectedRoster.department_id || null);
            }).catch(e => {
              if (e.response && e.response.data && e.response.data.errors) {
                Object.entries(e.response.data.errors).forEach(([field, msgs]) => {
                  this.$validator.errors.add({ field, msg: Array.isArray(msgs) ? msgs[0] : String(msgs) });
                });
              }
              this.$bvToast.toast('Failed to assign roster. Please try again.', { variant: 'danger' });
            }).finally(() => {
              this.loading = false;
            });
      });
    },

    resetForm(){
      this.form = { roster_id: null, branch_id: null, period_start: null, period_end: null };
    },
  },
  created() {
    this.getEmployees();
    this.getRosters();
  },
  beforeDestroy() {
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }
};
</script>

<!-- <style scoped>
.palette {
  min-height: 220px; border: 1px dashed #dee2e6; padding: 8px; border-radius: 8px; background: #fafbfc; max-height: 560px; overflow: auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
.dropzone { min-height: 140px; border: 1px dashed #c9d6f0; padding: 8px; border-radius: 8px; background: #f7faff; }
.pill { display: flex; align-items: center; gap: 8px; padding: 8px 10px;  border: 1px solid #e1e5ee; border-radius: 14px; background: #fff; }
.pill .handle { cursor: grab; color: #6c757d; }
.empty { color: #9aa0a6; font-size: 12px; padding: 6px; text-align: center; }
.trash { border-top: 1px solid #eee; padding-top: 8px; }
.trash-label { font-size: 12px; color: #6c757d; display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.trash-drop { min-height: 40px; border: 1px dashed #e9a5a5; background: #fff6f6; border-radius: 6px; }
.no-drag { pointer-events: auto; }
</style> -->
